//@flow

// Vendors
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

// Scandit
import {
  Brush,
  Camera,
  Color,
  DataCaptureContext,
  DataCaptureView,
  FrameSourceState,
} from '@scandit/web-datacapture-core';
import {
  DriverLicense,
  IdCapture,
  IdCaptureOverlay,
  IdCaptureScanner,
  IdCaptureSettings,
  idCaptureLoader,
  Region,
  SingleSideScanner,
} from '@scandit/web-datacapture-id';

// Requests
import {
  SCAN_STATUS,
  createScanTimestamp,
  createScanTransactionId,
  recordIdScanUsageRequest,
} from '../backend/productUsageRequests';

// Helpers
import {
  BARCODE_SCAN_EVENTS,
  trackBarcodeScanEvent,
} from '../lib/helpers/barcodeScanAnalytics';
import { extractCapturedIdFields } from '../lib/helpers/scanditCapturedId';

type Props = {
  enabled: boolean,
  onCapture?: Function,
  recordUsage?: boolean,
};

// Delay (ms) before handing the result to onCapture; 0 = immediate.
const SUCCESS_DISPLAY_DURATION = 0;

// Scandit errors expose `code`; DOM/getUserMedia errors expose `name`.
const getErrorCode = error =>
  (error && (error.code || error.name)) || 'UNKNOWN_ERROR';

export const CAMERA_ACCESS_DENIED = 'CAMERA_ACCESS_DENIED';

// PermissionDeniedError is the legacy name used by older browsers.
const CAMERA_DENIED_NAMES = ['NotAllowedError', 'PermissionDeniedError'];

const isCameraAccessDenied = error =>
  !!error &&
  (CAMERA_DENIED_NAMES.includes(error.name) ||
    CAMERA_DENIED_NAMES.includes(error.code) ||
    // Some browsers/SDK wrappers only signal denial in the message text.
    /permission denied|not allowed/i.test(error.message || ''));

// Maps an error to i18n keys; camera denial gets its own message so users know to enable access.
const toLoadError = error =>
  isCameraAccessDenied(error)
    ? {
      title: { file: 'Errors', id: 'CameraAccessDeniedTitle' },
      message: { file: 'Errors', id: 'CameraAccessDenied' },
      code: CAMERA_ACCESS_DENIED,
    }
    : {
      message: { file: 'Errors', id: 'BarcodeScannerLoadError' },
      code: getErrorCode(error),
      detail: error && error.message,
    };

// Scandit license/mode errors; replaces Scandit's own in-view message (hidden via CSS).
const toScanditLoadError = ({ code, message }) => ({
  message: { file: 'Errors', id: 'BarcodeScannerLoadError' },
  code,
  detail: message,
});

const silently = async operation => {
  try {
    await operation();
  } catch {
    // ignore - best-effort cleanup
  }
};

// The view keeps its last rendered frame, which would flash as a stale photo
// the next time the modal opens.
const clearRenderedFrame = element => {
  if (!element) return;
  element.querySelectorAll('canvas').forEach(canvas => {
    const context = canvas.getContext('2d');
    if (context) context.clearRect(0, 0, canvas.width, canvas.height);
  });
};

// Two rAFs guarantee the browser has painted at least one frame.
const nextFrame = () =>
  new Promise(resolve => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
  });

const useDLIDBarcodeScanner = ({
  enabled,
  onCapture,
  recordUsage = true,
}: Props) => {
  const dispatch = useDispatch();
  const scannerElementRef = useRef(null);
  // Engine/camera resources persist across close/reopen cycles - only the
  // camera stream and mode are toggled off/on, since iOS repeatedly
  // acquiring/releasing getUserMedia (via a fresh context each open) is what
  // caused the recurring scanner failures on rapid open/close.
  const resourcesRef = useRef({
    camera: null,
    view: null,
    context: null,
    idCapture: null,
  });
  const successTimerRef = useRef(null);
  // One transaction ID per scan attempt, shared by analytics and usage reporting.
  const scanTransactionRef = useRef(null);
  const scanHandledRef = useRef(false);
  const operationQueueRef = useRef(Promise.resolve());
  // Set when Scandit reports an error that stops scanning (e.g. expired key,
  // key missing the Parser feature) so the camera is not (re)started.
  const blockingErrorRef = useRef(null);
  const statusListenerRef = useRef(null);
  // Refs let long-lived SDK listeners read the latest props without re-initializing.
  const onCaptureRef = useRef(onCapture);
  onCaptureRef.current = onCapture;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const [scannerError, setScannerError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // The UI shows a generic message, so log the underlying cause for debugging.
  useEffect(() => {
    if (loadError)
      console.warn(
        '[Barcode scanner] load error:',
        loadError.code,
        loadError.detail || ''
      );
  }, [loadError]);

  // Analytics for every attempt; billable usage only for successes. Must never break the scan flow.
  const reportScan = status => {
    try {
      const transactionId =
        scanTransactionRef.current || createScanTransactionId();
      scanTransactionRef.current = transactionId;
      const timestamp = createScanTimestamp();
      //GA analytics report
      dispatch(
        trackBarcodeScanEvent(
          status === SCAN_STATUS.SUCCESS
            ? BARCODE_SCAN_EVENTS.SUCCESS
            : BARCODE_SCAN_EVENTS.FAILED
        )
      );
      if (status !== SCAN_STATUS.SUCCESS || !recordUsage) return;
      const action = recordIdScanUsageRequest(transactionId, status, timestamp);
      // Fire-and-forget; the user flow continues even if reporting fails.
      Promise.resolve(dispatch(action)).catch(error => {
        const responseStatus = error && error.response && error.response.status;
        console.warn(
          'ID scan usage reporting failed',
          responseStatus || (error && error.message)
        );
      });
    } catch {
      console.warn('ID scan usage reporting failed');
    }
  };

  // Resets per-attempt state before capture is (re)enabled.
  const prepareScan = () => {
    scanHandledRef.current = false;
    try {
      scanTransactionRef.current = createScanTransactionId();
    } catch {
      scanTransactionRef.current = null;
      console.warn('ID scan usage transaction ID generation failed');
    }
  };

  const clearSuccessTimer = () => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
  };

  // Full teardown - only used on unmount or after a failed (re)initialization.
  const teardown = useCallback(async () => {
    clearSuccessTimer();
    setIsLoading(false);
    const { camera, view, context, idCapture } = resourcesRef.current;
    // Clear first so the next activate() re-initializes instead of reusing disposed objects.
    resourcesRef.current = {
      camera: null,
      view: null,
      context: null,
      idCapture: null,
    };

    if (idCapture) await silently(() => idCapture.setEnabled(false));
    if (camera)
      await silently(() => camera.switchToDesiredState(FrameSourceState.Off));
    if (context) {
      await silently(async () => {
        // Release the camera before disposing the engine.
        await context.setFrameSource(null);
        await context.dispose();
      });
    }
    if (view) await silently(() => view.detachFromElement());
  }, []);

  // Turns the camera off but keeps the engine/context alive so reopening the
  // modal is a fast on/off toggle instead of a full re-acquire of the camera.
  const deactivate = useCallback(async () => {
    clearSuccessTimer();
    setScannerError(null);
    setLoadError(null);
    const { camera, view, idCapture } = resourcesRef.current;
    if (idCapture) await silently(() => idCapture.setEnabled(false));
    if (camera)
      await silently(() => camera.switchToDesiredState(FrameSourceState.Off));
    clearRenderedFrame(scannerElementRef.current);
    if (view) await silently(() => view.detachFromElement());
  }, []);

  // Serialize activate/deactivate/teardown calls so a camera state change
  // never overlaps a previous one still in flight.
  const enqueue = useCallback(operation => {
    // Run on both fulfil and reject so one failure doesn't stall the queue.
    const next = operationQueueRef.current.then(operation, operation);
    operationQueueRef.current = next.catch(() => { });
    return next;
  }, []);

  const initialize = useCallback(async () => {
    const licenseKey =
      window.scanditLicenseKey || process.env.REACT_APP_SCANDIT_LICENSE_KEY;
    if (!licenseKey) {
      setLoadError({
        title: { file: 'Errors', id: 'BarcodeScannerMissingLicense' },
        code: 'MISSING_LICENSE_KEY',
      });
      return false;
    }

    setLoadError(null);
    setIsLoading(true);
    blockingErrorRef.current = null;

    const failScanner = ({ code, message }) => {
      if (blockingErrorRef.current) return;
      dispatch(
        trackBarcodeScanEvent(BARCODE_SCAN_EVENTS.FAILED, { errorCode: code })
      );
      const error = toScanditLoadError({ code, message });
      blockingErrorRef.current = error;
      setIsLoading(false);
      setLoadError(error);
      // Can arrive after the camera is live, so release it too.
      enqueue(teardown);
    };

    try {
      // Reports license problems (e.g. expired key 131674, unlicensed domain).
      // An invalid status means Scandit processes no frames.
      const statusListener = {
        didChangeStatus: (ctx, contextStatus) => {
          if (!contextStatus.isValid) failScanner(contextStatus);
        },
      };
      // forLicenseKey() initializes this shared instance and reports the
      // license status before resolving, so the listener must be added first.
      const sharedContext = DataCaptureContext.sharedInstance;
      if (statusListenerRef.current)
        sharedContext.removeListener(statusListenerRef.current);
      sharedContext.addListener(statusListener);
      statusListenerRef.current = statusListener;

      const view = new DataCaptureView();
      const context = await DataCaptureContext.forLicenseKey(licenseKey, {
        // Served from public/, populated by scripts/copy-scandit-id-engine.js on postinstall.
        libraryLocation: '/scandit-engine/',
        // VIZ disabled — barcode-only flow; avoids loading VIZ engine resources
        moduleLoaders: [idCaptureLoader({ enableVIZDocuments: false })],
      });

      // Rear camera on mobile; does not start the stream or prompt for permission yet.
      const camera = Camera.pickBestGuess();
      await camera.applySettings(IdCapture.recommendedCameraSettings);
      // Camera frames are fed into the engine for processing.
      await context.setFrameSource(camera);
      // View renders the camera preview and overlays for this context.
      await view.setContext(context);

      const settings = new IdCaptureSettings();
      settings.scanner = new IdCaptureScanner({
        // Args: barcode, MRZ, VIZ - read only the PDF417 barcode on the back.
        physicalDocument: new SingleSideScanner(true, false, false),
      });
      settings.acceptedDocuments = [new DriverLicense(Region.Any)];

      const idCapture = await IdCapture.forContext(context, settings);

      idCapture.addListener({
        didCaptureId: async capturedId => {
          // Engine may fire again before it is disabled; handle only the first result.
          if (scanHandledRef.current) return;
          scanHandledRef.current = true;

          const {
            firstName,
            lastName,
            dateOfBirth,
            licenseNumber,
            auditNum,
          } = extractCapturedIdFields(capturedId);

          // Barcode decoded but missing the fields the backend needs.
          if (!licenseNumber || !auditNum) {
            reportScan(SCAN_STATUS.FAILURE);
            await idCapture.setEnabled(false);
            setScannerError({
              title: { file: 'Errors', id: 'BarcodeNotFoundTitle' },
              message: { file: 'Errors', id: 'BarcodeNotFound' },
            });
            return;
          }

          const capture = {
            firstName,
            lastName,
            dateOfBirth,
            licenseNumber,
            // Form fields and Redux pii use `dlNumber`.
            dlNumber: licenseNumber,
            auditNum,
          };

          reportScan(SCAN_STATUS.SUCCESS);
          // Stop processing frames so the same license isn't captured again.
          await idCapture.setEnabled(false);

          clearSuccessTimer();
          successTimerRef.current = setTimeout(() => {
            successTimerRef.current = null;
            if (onCaptureRef.current) onCaptureRef.current(capture);
          }, SUCCESS_DISPLAY_DURATION);
        },
        // Document detected but not accepted (e.g. not a driver's license).
        didRejectId: async () => {
          if (scanHandledRef.current) return;
          scanHandledRef.current = true;
          reportScan(SCAN_STATUS.FAILURE);
          await idCapture.setEnabled(false);
          setScannerError({
            title: { file: 'Errors', id: 'BarcodeNotFoundTitle' },
            message: { file: 'Errors', id: 'BarcodeNotFound' },
          });
        },
        // Mode-level failures (e.g. key lacks the Parser feature needed to
        // read DL barcodes) are not reported through the context status.
        didFailWithError: (ic, error) => {
          const code = (error && error.type) || getErrorCode(error);
          if (code === 'RecoveredAfterFailure') {
            console.warn('[Barcode scanner] recovered after failure');
            return;
          }
          failScanner({ code, message: error && error.message });
        },
      });

      const idCaptureOverlay = await IdCaptureOverlay.withIdCaptureForView(
        idCapture,
        view
      );
      // Our own UI shows guidance, so hide Scandit's built-in hint text.
      idCaptureOverlay.showTextHints = false;
      // White while scanning, hidden on successful capture.
      const localizedBrush = new Brush(
        Color.fromHex('FFFFFF22'),
        Color.fromHex('FFFFFFAA'),
        4
      );
      const capturedBrush = new Brush(
        Color.fromHex('00000000'),
        Color.fromHex('00000000'),
        0
      );
      await idCaptureOverlay.setLocalizedBrush(localizedBrush);
      await idCaptureOverlay.setCapturedBrush(capturedBrush);
      // Stay idle until activate() turns the camera on.
      await idCapture.setEnabled(false);

      resourcesRef.current = { camera, view, context, idCapture };
      setIsLoading(false);
      // Queued teardown disposes these resources; don't start the camera.
      if (blockingErrorRef.current) return false;
      return true;
    } catch (error) {
      setIsLoading(false);
      resourcesRef.current = {
        camera: null,
        view: null,
        context: null,
        idCapture: null,
      };
      const errorCode = getErrorCode(error);
      dispatch(
        trackBarcodeScanEvent(BARCODE_SCAN_EVENTS.FAILED, { errorCode })
      );

      setLoadError(toLoadError(error));
      return false;
    }
  }, [dispatch, enqueue, teardown]);

  const activate = useCallback(async () => {
    const scannerElement = scannerElementRef.current;
    if (!scannerElement || !enabledRef.current) return;

    setScannerError(null);
    setIsLoading(true);
    // Hidden until the live stream paints, to avoid flashing an empty/stale view.
    scannerElement.style.visibility = 'hidden';

    // First open (or after teardown) - load the engine; later opens reuse it.
    if (!resourcesRef.current.context) {
      const initialized = await initialize();
      if (!initialized) {
        scannerElement.style.visibility = '';
        return;
      }
      // initialize() clears loading; keep the spinner until the camera is live.
      setIsLoading(true);
    }

    const { camera, view, idCapture } = resourcesRef.current;
    // Disabled while the engine loaded (e.g. session failed) - don't trigger the camera prompt.
    if (!camera || !view || !idCapture || !enabledRef.current) {
      setIsLoading(false);
      scannerElement.style.visibility = '';
      return;
    }

    try {
      view.connectToElement(scannerElement);
      clearRenderedFrame(scannerElement);
      // Discard any partial result from the previous session.
      await idCapture.reset();
      // Starts the stream; the browser permission prompt appears here on first use.
      await camera.switchToDesiredState(FrameSourceState.On);
      prepareScan();
      await idCapture.setEnabled(true);
      // Let the live stream paint before revealing the viewport.
      await nextFrame();
      scannerElement.style.visibility = '';
      setIsLoading(false);
    } catch (error) {
      scannerElement.style.visibility = '';
      const errorCode = getErrorCode(error);
      dispatch(
        trackBarcodeScanEvent(BARCODE_SCAN_EVENTS.FAILED, { errorCode })
      );
      await teardown();
      setLoadError(toLoadError(error));
    }
  }, [initialize, teardown, dispatch]);

  // Re-arms capture after a failed scan.
  const retry = useCallback(
    () =>
      enqueue(async () => {
        const { camera, idCapture } = resourcesRef.current;
        if (!camera || !idCapture) return;
        setScannerError(null);
        try {
          // A live stream survives permission revocation, so re-acquire the camera to re-check access.
          await camera.switchToDesiredState(FrameSourceState.Off);
          await idCapture.reset();
          await camera.switchToDesiredState(FrameSourceState.On);
          prepareScan();
          await idCapture.setEnabled(true);
        } catch (error) {
          dispatch(
            trackBarcodeScanEvent(BARCODE_SCAN_EVENTS.FAILED, {
              errorCode: getErrorCode(error),
            })
          );
          await teardown();
          setLoadError(toLoadError(error));
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, enqueue, teardown]
  );

  // Full restart from scratch, e.g. after a load error or camera permission change.
  const reload = useCallback(() => {
    setLoadError(null);
    enqueue(teardown).then(() => enqueue(activate));
  }, [activate, enqueue, teardown]);

  useEffect(() => {
    if (enabled) {
      enqueue(activate);
    } else {
      enqueue(deactivate);
    }
  }, [activate, deactivate, enabled, enqueue]);

  // Full teardown only on final unmount, not on every close.
  useEffect(() => {
    return () => {
      enqueue(teardown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    scannerElementRef,
    scannerError,
    loadError,
    isLoading,
    dispose: teardown,
    retry,
    reload
  };
};

export default useDLIDBarcodeScanner;
